
/**
 * Scroll to a specific section on the page with a smooth animation effect
 * @param sectionId The ID of the HTML element to scroll to
 */
export const scrollToSection = (sectionId: string) => {
  // Check if the page has already loaded
  if (document.readyState === 'complete') {
    scrollToElementById(sectionId);
  } else {
    // If the page is still loading, wait for it to complete
    window.addEventListener('load', () => {
      scrollToElementById(sectionId);
    }, { once: true });
  }
};

/**
 * Helper function to handle the actual scrolling to an element
 * @param elementId The ID of the element to scroll to
 */
const scrollToElementById = (elementId: string) => {
  // Small delay to ensure DOM is fully rendered
  setTimeout(() => {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    } else {
      console.error(`Element with id '${elementId}' not found`);
    }
  }, 100); // Reduced delay for faster response
};

/**
 * Navigate to another page and scroll to a specific section
 * @param url The URL of the page to navigate to
 * @param sectionId The ID of the section to scroll to
 */
export const navigateToSection = (url: string, sectionId: string) => {
  // Store the section ID in session storage
  sessionStorage.setItem('scrollToSection', sectionId);
  
  // Navigate to the specified URL
  window.location.href = url;
};

/**
 * Handles hash-based navigation when the page loads
 * This checks if there's a hash in the URL and scrolls to that section
 */
export const handleHashBasedNavigation = () => {
  // First check if we have a stored section ID from a programmatic navigation
  const storedSectionId = sessionStorage.getItem('scrollToSection');
  
  if (storedSectionId) {
    // Clear the stored section ID
    sessionStorage.removeItem('scrollToSection');
    scrollToSection(storedSectionId);
  } else if (window.location.hash) {
    // Extract the section ID from the hash (remove the # symbol)
    const sectionId = window.location.hash.substring(1);
    scrollToSection(sectionId);
  }
};

// Automatically set up hash-based navigation when this module is imported
if (typeof window !== 'undefined') {
  // Add an event listener for when the page has loaded
  window.addEventListener('DOMContentLoaded', handleHashBasedNavigation);
  
  // Also handle navigation when the hash changes without a page reload
  window.addEventListener('hashchange', handleHashBasedNavigation);
}
